// Plato Doctor Mapping Manager
// Admin interface for mapping Plato calendars to Twin City doctors

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, Link2, Unlink, Trash2, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import {
  fetchPlatoCalendars,
  getAllDoctorMappingsForClinic,
  upsertDoctorMapping,
  deleteDoctorMapping,
} from '@/services/platoService';
import { getDoctors, Doctor } from '@/services/adminService';
import type { PlatoCalendar, PlatoDoctorMapping } from '@/types/plato';

interface PlatoDoctorMappingManagerProps {
  clinicId: string;
}

interface MappedCalendar {
  calendar: PlatoCalendar;
  mapping: PlatoDoctorMapping | null;
  twinCityDoctor: Doctor | null;
}

const PlatoDoctorMappingManager = ({ clinicId }: PlatoDoctorMappingManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [savingCalendarId, setSavingCalendarId] = useState<string | null>(null);

  // Fetch Plato calendars
  const {
    data: platoCalendars,
    isLoading: isLoadingCalendars,
    error: calendarsError,
    refetch: refetchCalendars,
  } = useQuery({
    queryKey: ['platoCalendars'],
    queryFn: fetchPlatoCalendars,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch existing mappings
  const {
    data: existingMappings,
    isLoading: isLoadingMappings,
  } = useQuery({
    queryKey: ['platoDoctorMappings', clinicId],
    queryFn: () => getAllDoctorMappingsForClinic(clinicId),
    enabled: !!clinicId,
  });

  // Fetch Twin City doctors
  const {
    data: twinCityDoctors,
    isLoading: isLoadingDoctors,
  } = useQuery({
    queryKey: ['doctors', clinicId],
    queryFn: () => getDoctors(clinicId),
    enabled: !!clinicId,
  });

  // Mutation for saving/updating mapping
  const saveMappingMutation = useMutation({
    mutationFn: async ({
      platoCalendarId,
      platoDoctorName,
      twinCityDoctorId,
    }: {
      platoCalendarId: string;
      platoDoctorName: string;
      twinCityDoctorId: string;
    }) => {
      return upsertDoctorMapping({
        platoCalendarId,
        platoDoctorName,
        twinCityDoctorId,
        twinCityClinicId: clinicId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platoDoctorMappings', clinicId] });
      toast({
        title: 'Mapping Saved',
        description: 'Doctor mapping has been saved successfully.',
      });
      setSavingCalendarId(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save mapping.',
        variant: 'destructive',
      });
      setSavingCalendarId(null);
    },
  });

  // Mutation for deleting mapping
  const deleteMappingMutation = useMutation({
    mutationFn: deleteDoctorMapping,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platoDoctorMappings', clinicId] });
      toast({
        title: 'Mapping Removed',
        description: 'Doctor mapping has been removed.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove mapping.',
        variant: 'destructive',
      });
    },
  });

  // Combine calendars with their mappings
  const mappedCalendars: MappedCalendar[] = (platoCalendars || []).map((calendar) => {
    const mapping = existingMappings?.find(
      (m) => m.plato_calendar_id === calendar.id
    ) || null;
    const twinCityDoctor = mapping
      ? twinCityDoctors?.find((d) => d.id === mapping.twin_city_doctor_id) || null
      : null;
    return { calendar, mapping, twinCityDoctor };
  });

  // Handle doctor selection
  const handleDoctorSelect = (calendarId: string, calendarName: string, doctorId: string) => {
    if (doctorId === 'none') {
      // Find and delete the mapping
      const mapping = existingMappings?.find((m) => m.plato_calendar_id === calendarId);
      if (mapping) {
        deleteMappingMutation.mutate(mapping.id);
      }
    } else {
      setSavingCalendarId(calendarId);
      saveMappingMutation.mutate({
        platoCalendarId: calendarId,
        platoDoctorName: calendarName,
        twinCityDoctorId: doctorId,
      });
    }
  };

  // Loading state
  const isLoading = isLoadingCalendars || isLoadingMappings || isLoadingDoctors;

  // Count stats
  const totalCalendars = platoCalendars?.length || 0;
  const mappedCount = mappedCalendars.filter((mc) => mc.mapping !== null).length;
  const unmappedCount = totalCalendars - mappedCount;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Plato Doctor Mapping
            </CardTitle>
            <CardDescription>
              Map Plato calendars to Twin City doctors for appointment sync
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchCalendars()}
            disabled={isLoadingCalendars}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingCalendars ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats */}
        <div className="flex gap-4 mb-4">
          <Badge variant="outline" className="px-3 py-1">
            Total: {totalCalendars}
          </Badge>
          <Badge variant="default" className="px-3 py-1 bg-green-600">
            <CheckCircle className="h-3 w-3 mr-1" />
            Mapped: {mappedCount}
          </Badge>
          {unmappedCount > 0 && (
            <Badge variant="destructive" className="px-3 py-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              Unmapped: {unmappedCount}
            </Badge>
          )}
        </div>

        {/* Error state */}
        {calendarsError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to fetch Plato calendars. Please check your connection and try again.
              <br />
              <span className="text-xs opacity-70">
                {calendarsError instanceof Error ? calendarsError.message : 'Unknown error'}
              </span>
            </AlertDescription>
          </Alert>
        )}

        {/* Unmapped warning */}
        {unmappedCount > 0 && !calendarsError && (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {unmappedCount} calendar{unmappedCount > 1 ? 's are' : ' is'} not mapped.
              Appointments from unmapped calendars will be skipped during sync.
            </AlertDescription>
          </Alert>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading...</span>
          </div>
        ) : mappedCalendars.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No Plato calendars found. Click Refresh to fetch from Plato API.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plato Calendar</TableHead>
                <TableHead>Calendar ID</TableHead>
                <TableHead>Twin City Doctor</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappedCalendars.map(({ calendar, mapping, twinCityDoctor }, index) => (
                <TableRow key={calendar.id || `calendar-${index}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {calendar.color && (
                        <div
                          className="w-3 h-3 rounded-full border"
                          style={{ backgroundColor: calendar.color }}
                        />
                      )}
                      {calendar.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1 py-0.5 rounded">
                      {calendar.id}
                    </code>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={mapping?.twin_city_doctor_id || 'none'}
                      onValueChange={(value) =>
                        handleDoctorSelect(calendar.id, calendar.name, value)
                      }
                      disabled={savingCalendarId === calendar.id}
                    >
                      <SelectTrigger className="w-full sm:w-[200px]">
                        {savingCalendarId === calendar.id ? (
                          <div className="flex items-center">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Saving...
                          </div>
                        ) : (
                          <SelectValue placeholder="Select doctor" />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">
                          <span className="text-muted-foreground">Not Mapped</span>
                        </SelectItem>
                        {twinCityDoctors
                          ?.filter((d) => d.status === 'active')
                          .map((doctor) => (
                            <SelectItem key={doctor.id} value={doctor.id}>
                              {doctor.name}
                              {doctor.specialization && (
                                <span className="text-muted-foreground ml-1">
                                  ({doctor.specialization})
                                </span>
                              )}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-center">
                    {mapping ? (
                      <Badge variant="default" className="bg-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Mapped
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <Unlink className="h-3 w-3 mr-1" />
                        Unmapped
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {mapping && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMappingMutation.mutate(mapping.id)}
                        disabled={deleteMappingMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default PlatoDoctorMappingManager;
